import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./auth";
import { confirmLeave } from "../lib/use-leave-guard";

type ViewMode = "admin" | "user";

interface ViewModeContextType {
  viewMode: ViewMode;
  toggleViewMode: () => void;
  switchToAdmin: () => void;
  switchToUser: () => void;
  isAdminViewingAsUser: boolean;
}

export const ViewModeContext = createContext<ViewModeContextType>({
  viewMode: "user",
  toggleViewMode: () => {},
  switchToAdmin: () => {},
  switchToUser: () => {},
  isAdminViewingAsUser: false,
});

export const ViewModeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isAdmin = user?.userType === "admin";

  const [viewMode, setViewMode] = useState<ViewMode>(
    isAdmin ? "admin" : "user",
  );

  // Each switch changes the mode before it navigates, so it asks first: a
  // "stay" (mid-upload) must leave the mode as it was, not just the page.
  const switchToAdmin = useCallback(() => {
    if (!isAdmin || !confirmLeave()) return;
    setViewMode("admin");
    navigate("/admin/home");
  }, [isAdmin, navigate]);

  const switchToUser = useCallback(() => {
    if (!isAdmin || !confirmLeave()) return;
    setViewMode("user");
    navigate("/sessions");
  }, [isAdmin, navigate]);

  const toggleViewMode = useCallback(() => {
    if (!isAdmin) return;
    if (viewMode === "admin") {
      switchToUser();
    } else {
      switchToAdmin();
    }
  }, [isAdmin, viewMode, switchToAdmin, switchToUser]);

  const isAdminViewingAsUser = isAdmin && viewMode === "user";

  const value = useMemo(
    () => ({ viewMode, toggleViewMode, switchToAdmin, switchToUser, isAdminViewingAsUser }),
    [viewMode, toggleViewMode, switchToAdmin, switchToUser, isAdminViewingAsUser],
  );

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
};
